import { Typography } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import LoadingAnimation from "./LoadingAnimation";

const { Title } = Typography;

interface FullscreenLoadingProps {
  message?: string;
  backgroundColor?: string;
  textColor?: string;
  animationType?: "dots" | "spinner" | "pulse" | "particles";
  animationSize?: number;
  animationColor?: string;
}

// define animations
const animations = `
  @keyframes textPulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }

  @keyframes float {
    0%, 100% {
      transform: translate(0, 0) rotate(0deg);
    }
    25% {
      transform: translate(10px, -20px) rotate(5deg);
    }
    50% {
      transform: translate(-15px, -10px) rotate(-5deg);
    }
    75% {
      transform: translate(20px, 15px) rotate(3deg);
    }
  }

  @keyframes float-reverse {
    0%, 100% {
      transform: translate(0, 0) rotate(0deg);
    }
    25% {
      transform: translate(-15px, 20px) rotate(-4deg);
    }
    50% {
      transform: translate(20px, 10px) rotate(6deg);
    }
    75% {
      transform: translate(-10px, -15px) rotate(-3deg);
    }
  }

  @keyframes float-slow {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(-20px, 15px) scale(1.1);
    }
    66% {
      transform: translate(15px, -20px) scale(0.9);
    }
  }
`;

/**
 * fullscreen loading component
 *
 * @param message - loading message
 * @param backgroundColor - background color
 * @param textColor - text color
 * @param animationType - animation type
 * @param animationSize - animation size
 * @param animationColor - animation color
 */
const FullscreenLoading: React.FC<FullscreenLoadingProps> = ({
  message = "Loading...",
  backgroundColor,
  textColor = "#334155",
  animationType = "particles",
  animationSize = 60,
  animationColor = "#3b82f6",
}) => {
  const [opacity, setOpacity] = useState(0);

  // add fade in effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpacity(1);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>{animations}</style>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            backgroundColor || "linear-gradient(135deg, #ffffff 0%, #f0f9ff 50%, #e0f2fe 100%)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          zIndex: 1000,
          opacity,
          transition: "opacity 0.3s ease-in-out",
          overflow: "hidden",
        }}
      >
        {/* Floating background shapes */}
        <div
          style={{
            position: "absolute",
            top: "10%",
            left: "15%",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(59, 130, 246, 0.15), rgba(147, 197, 253, 0.05))",
            filter: "blur(40px)",
            animation: "float 20s ease-in-out infinite",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "60%",
            right: "10%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(168, 85, 247, 0.12), rgba(196, 181, 253, 0.04))",
            filter: "blur(50px)",
            animation: "float-reverse 25s ease-in-out infinite",
            animationDelay: "2s",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "10%",
            left: "10%",
            width: "350px",
            height: "350px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(14, 165, 233, 0.1), rgba(125, 211, 252, 0.03))",
            filter: "blur(45px)",
            animation: "float-slow 30s ease-in-out infinite",
            animationDelay: "5s",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "30%",
            right: "25%",
            width: "250px",
            height: "250px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(99, 102, 241, 0.08), rgba(165, 180, 252, 0.02))",
            filter: "blur(35px)",
            animation: "float 18s ease-in-out infinite",
            animationDelay: "7s",
            zIndex: 1,
          }}
        />

        {/* Main content */}
        <div
          className="loading-container"
          style={{ textAlign: "center", position: "relative", zIndex: 10 }}
        >
          <LoadingAnimation type={animationType} size={animationSize} color={animationColor} />
          {message && (
            <Title
              level={3}
              style={{
                marginTop: 20,
                fontWeight: 500,
                color: textColor,
                animation: "textPulse 1.5s infinite ease-in-out",
              }}
            >
              {message}
            </Title>
          )}
        </div>
      </div>
    </>
  );
};

export default FullscreenLoading;
